"use client";

import type { BlockConfig } from "@/lib/registry/types";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { getDynamicDemoComponent } from "../_utils/load-demo.tsx";
import type React from "react";
import { Suspense, useEffect, useState } from "react";
import { SuspenseFallback } from "@/components/primitives/suspense-fallback.tsx";
import { ComponentErrorBoundary } from "@/components/primitives/component-error-boundary";

interface ComponentPreviewProps {
  component: BlockConfig & {
    previewProps?: {
      previewPath: string;
      hasPreviewImage: boolean;
      availablePreviewFiles: string[];
      demoFiles: string[];
    };
  };
  className?: string;
  mode?: "image" | "demo" | "auto";
}

/**
 * Client component that renders either a demo or an image preview of a component
 * Dynamically loads the demo component on the client side.
 */
export function ComponentPreview({
  component,
  className,
  mode = "auto",
}: ComponentPreviewProps) {
  const [DemoComponent, setDemoComponent] = useState<
    React.ComponentType | null | "loading" | "error"
  >("loading");

  const placeholder = (
    <div className="flex h-full w-full items-center justify-center">
      <div className="text-sm text-muted-foreground">{component.name}</div>
    </div>
  );

  // Extract preview props safely
  const previewPath = component?.previewProps?.previewPath || "";
  const hasPreviewImage = component?.previewProps?.hasPreviewImage || false;
  const demoFiles = component?.previewProps?.demoFiles || [];

  // Determine which mode to render
  const shouldTryDemo =
    (mode === "auto" || mode === "demo") && demoFiles.length > 0;

  // Determine if we should show the image preview
  let shouldShowImage = false;
  if (mode === "image" && hasPreviewImage) {
    shouldShowImage = true;
  } else if (mode === "auto") {
    // In auto mode, show image if we shouldn't try demo OR if demo is not available
    if (!shouldTryDemo || demoFiles.length === 0) {
      shouldShowImage = hasPreviewImage;
    }
  }

  useEffect(() => {
    let isMounted = true;
    if (shouldTryDemo) {
      setDemoComponent("loading");
      try {
        const demoFile = demoFiles[0];
        const LoadedComponent = getDynamicDemoComponent(component, demoFile);
        if (isMounted && LoadedComponent) {
          if (typeof LoadedComponent === "function") {
            setDemoComponent(() => LoadedComponent as React.ComponentType);
          } else {
            console.error(
              "Loaded component is not a function:",
              LoadedComponent
            );
            setDemoComponent("error");
          }
        } else if (isMounted) {
          setDemoComponent("error");
        }
      } catch (error) {
        console.error(
          `Error preparing dynamic component for ${component.name}:`,
          error
        );
        if (isMounted) {
          setDemoComponent("error");
        }
      }
    }
    return () => {
      isMounted = false;
    };
    // Depend only on component identifier and mode to load component
  }, [
    component.library,
    component.name,
    demoFiles.join(),
    mode,
    shouldTryDemo,
  ]);

  // Render demo if loaded and appropriate
  if (shouldTryDemo && typeof DemoComponent === "function") {
    return (
      <div className={cn("grid h-auto w-full relative", className)}>
        <ComponentErrorBoundary
          componentName={component.name}
          className="h-full w-full"
          showError={true}
        >
          <Suspense fallback={<SuspenseFallback />}>
            <DemoComponent />
          </Suspense>
        </ComponentErrorBoundary>
      </div>
    );
  }

  // Show loading state for demo
  if (shouldTryDemo && DemoComponent === "loading") {
    return <SuspenseFallback />; // Show loading indicator while demo loads
  }

  // Show error state for demo if loading failed and mode is strictly demo
  if (shouldTryDemo && DemoComponent === "error" && mode === "demo") {
    return (
      <div className="grid h-full w-full bg-muted/10">
        <div className="text-sm text-muted-foreground">Demo not available</div>
      </div>
    );
  }

  // Fallback to image if demo failed/errored (and mode is auto) or wasn't requested, or if mode is image
  if (
    shouldShowImage ||
    (shouldTryDemo && DemoComponent === "error" && mode === "auto")
  ) {
    if (hasPreviewImage) {
      return (
        <Image
          fill
          src={previewPath}
          alt={`Preview of ${component.name}`}
          className={cn(className, "object-cover")}
          priority={false}
        />
      );
    } else {
      // If shouldShowImage is true but no image exists, show placeholder
      return placeholder;
    }
  }

  // Default placeholder if nothing else renders
  return placeholder;
}
